/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1903460357 — PaymentScreen */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { usePageTracking } from '../../../../shared/hooks/usePageTracking';
import { debug, getVmDetails } from '../../../../shared/lib/general';
import { ContentBox, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { CancelPage } from '../../../event-log';
import { LoyaltyPaymentButton } from '../../../loyalty';
import { CancelButton } from '../../../order/components/CancelButton/CancelButton';
import { useOrderActions } from '../../../order/hooks/useOrderActions';
import { withOrderHydration } from '../../../order/hooks/withOrderHydration';
import { PaymentButtons } from '../../components/PaymentButtons';
import { PriceCT1 } from '../../components/PriceDisplay/PriceCT1/PriceCT1';
import { PricePopcorn } from '../../components/PriceDisplay/PricePopcorn/PricePopcorn';
import { usePopcornAmount } from '../../hooks/usePopcornAmount';
import { useTimerTimeout } from '../../hooks/useTimerTimeout';
import styles from './PaymentScreen.module.css';

interface PaymentScreenProps {
    screenName: CancelPage;
}

const PaymentScreenBody = ({ screenName }: PaymentScreenProps) => {
    const { t } = useTranslation();
    const { onTimerTimeoutHandler } = useOrderActions();
    const order = useAppStore((store) => store.order);
    const vm = useAppStore((store) => store.vm);

    const { isPopcorn: isPopcornVendingMachine, paymentTimeoutSec } =
        getVmDetails(vm);

    const timerDuration = paymentTimeoutSec;

    const { popcornDiscountedSubunits, popcornOriginalAmountCurrency } =
        usePopcornAmount();

    useTimerTimeout({
        screenName: isPopcornVendingMachine ? 'payment-popcorn' : 'payment',
        onTimeout: () => onTimerTimeoutHandler(screenName),
        duration: timerDuration,
    });

    usePageTracking('payment');

    useEffect(() => {
        //order change
        if (order) {
            debug('order change', 'data', {
                order,
            });
        }
    }, [order]);

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Order payment')}</ScreenTitle>
            <ContentBox>
                <img className={styles.cardImage} src="/card.svg" alt="." />

                {isPopcornVendingMachine ? (
                    <PricePopcorn
                        discountedAmountInSubunits={popcornDiscountedSubunits}
                        originalAmountInCurrency={popcornOriginalAmountCurrency}
                    />
                ) : (
                    <PriceCT1 />
                )}

                <div className={styles.paymentActions}>
                    <LoyaltyPaymentButton orderId={order.id} />
                    <PaymentButtons />
                </div>

                <CancelButton page={screenName} />
            </ContentBox>
        </ScreenWrapper>
    );
};

const PaymentScreen = withOrderHydration(PaymentScreenBody);

export { PaymentScreen };
